/**
 * The full-screen mobile search surface (M-1, §3.6).
 *
 * Replaces the no-op that shipped in `ListingsClient.handleSearchClick`
 * ("Will open search modal in a future PR") — the mobile top bar's search
 * trigger did nothing at all. Rails had a dedicated full-screen `/search`
 * overlay; this is that surface on this stack.
 *
 * It is STATE, not a route. `packages/routes/src/public.ts` records that
 * `/search` was deliberately deleted (the live URL 404s), so resurrecting a
 * route for it would re-introduce exactly what that removal cleaned up. An
 * overlay also keeps the page underneath mounted, so dismissing it costs
 * nothing.
 *
 * ⚠ IT IS THE ENHANCEMENT, NEVER THE SEARCH. This surface exists only once
 * JavaScript has run, and it could not be the no-JS path even if it were in the
 * served HTML: the `SearchBar` inside it is a CONTROLLED combobox, which
 * `packages/ui` gates `disabled` until hydration (LET-78) — and a disabled
 * control is NOT SUBMITTED. The no-JS search is `SearchForm`'s own native
 * `<form method="get">`, which this overlay is layered on top of. Round 1's
 * claim that the no-JS path was "a working page rather than a dead button"
 * rested on the trigger being an anchor to the browse HUB, and the hub's only
 * mobile search control was that same anchor — pointing at the page the reader
 * was already on. See `SearchForm.tsx`.
 *
 * No new suggestion plumbing: this hosts the same `SearchBar` the desktop
 * navbar and the home hero use, so all three surfaces share one debounce, one
 * min-query gate, and one set of href builders.
 *
 * ## Why this file composes almost nothing (R4-1)
 *
 * It used to wrap the field in a header `div` of its own — the row padding, the
 * gap, the hairline under it — and hang a trailing ✕ beside it. Both were the
 * same mistake in two sizes: a full-screen host describing geometry the
 * combobox's variant table owns. Measured at 393x852 the cost was the DESKTOP
 * dropdown inside a full-screen dialog (`position: absolute`, 321 px wide,
 * `max-height: 320px`, `shadow-lg`, a rounded card over ~500 px of white) and
 * two ✕ glyphs 64 px apart on one row.
 *
 * The `sheet` variant is the whole remedy: it declares an in-flow panel and a
 * muted field, and it therefore OWNS the field row — so the panel can fill a
 * box its own root spans, which is the one thing a wrapper `div` here made
 * impossible. What is left in this file is the surface and the two decisions
 * only a host can make: which variant it is, and what leads the row.
 *
 * ## Why it is an `entity` and not a listings component
 *
 * It was `features/storefront/listings/components/ListingsSearchOverlay.tsx`
 * for the whole of its first life, and that is why the HOME page still shipped
 * the desktop combobox inline on a 393 px phone: a cross-feature import is
 * refused by the element DAG, so the one full-screen search surface in the app
 * was unreachable from the one page that most needed it. The home hero and the
 * browse canvas are two features; a search surface both of them mount is not
 * either one's, so it lives beside `SearchBar` in `shared/components/search/`
 * — which is the directory the DAG's own header names as "where sub-feature
 * sharing belongs". Nothing about its behaviour changed in the move.
 *
 * The only consumer is {@link SearchForm}, which owns the `open` state that
 * feeds it. The props stay `open`/`onOpenChange`/`mode` rather than collapsing
 * into the field, so the surface is still testable on its own and a second host
 * (a keyboard shortcut, a deep link) needs no re-plumbing.
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import type { ListingsMode } from '@letsgethome/routes';
import { Button } from '@letsgethome/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@letsgethome/ui/sheet';
import { ArrowLeft } from 'lucide-react';

import { SearchBar } from './SearchBar';

export interface SearchOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Browse mode a selection stays on (`/listings/rent` vs `/buy`). */
  mode: ListingsMode;
}

export function SearchOverlay({ open, onOpenChange, mode }: SearchOverlayProps) {
  const t = useTranslations('listings');
  const tSearch = useTranslations('search');

  return (
    // `mobileOnly`, and NOT an `md:hidden` class on the content: the range has
    // to govern the sheet's OPEN state, because CSS cannot release Radix's
    // focus trap or its scroll lock. This surface stated it as `md:hidden` on
    // `<SheetContent>` alone while `<SheetOverlay>`'s `bg-black/80` scrim
    // carried no such class, so opening it on a phone and then widening past
    // `md` — a rotation is enough — left the reader looking at a dark screen
    // with no dialog on it and no way out.
    <Sheet open={open} onOpenChange={onOpenChange} mobileOnly>
      <SheetContent
        side="top"
        // Full-viewport: a search surface that leaves the map half-visible
        // invites taps that dismiss it mid-query.
        //
        // `flex flex-col` because the `sheet` combobox is an IN-FLOW list: it
        // fills the box its own root spans, and its root is this content's only
        // in-flow child. A panel cannot fill a box its root does not span —
        // which is why the field row's chrome moved INTO the primitive rather
        // than staying here as a wrapper `div`.
        className="flex h-full w-full max-w-none flex-col border-0 p-0"
        // ONE close, and it LEADS the search bar — the arrow every full-screen
        // mobile search puts before the field. The primitive used to inject its
        // own corner close as well, carrying a hard-coded English sr-only
        // "Close" — two dismiss controls one row apart, one of them
        // unlocalised. `closeSlot` is verified, not trusted: the primitive
        // throws if this subtree renders no `<SheetClose>`, and the close is
        // still drawn by the CONTENT — one row earlier than it was.
        closeSlot="content"
      >
        {/* The primitive requires a title/description for its dialog
            semantics; both are screen-reader-only because the input's own
            label already names the surface visually. */}
        <SheetTitle className="sr-only">{tSearch('placeholder')}</SheetTitle>
        <SheetDescription className="sr-only">{tSearch('placeholder')}</SheetDescription>

        <SearchBar
          // The variant IS the decision: `sheet` is the row of the combobox's
          // geometry table that declares an in-flow panel and a muted field.
          // It used to be `hero` — the home page's floating pill — so this
          // full-screen surface hosted the DESKTOP's dropdown: a 321 px card
          // with a shadow and a 320 px cap, floating over ~500 px of white
          // (R4-1).
          variant="sheet"
          autoFocus
          mode={mode}
          // `asChild`: the dismiss is Radix's own `Close`, so it works from
          // the primitive's side (and carries the `data-slot` the content
          // arm's assertion looks for) while staying the app's `Button`.
          //
          // An ARROW, not a cross, and it is not a navigation: `listings.searchClose`
          // stays its accessible name (zh-HK and zh-CN translations exist)
          // because this control CLOSES a surface. With a typed query the row
          // used to carry two ✕ glyphs 64 px apart — this one and the field's
          // own `Clear search` — which said the same thing twice and meant two
          // different things.
          leading={
            <SheetClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('searchClose')}
                className="h-10 w-10 shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </SheetClose>
          }
          // Dismiss once a suggestion has been chosen — the navigation is
          // already under way, and leaving the overlay up would cover the
          // results the user just asked for.
          onSelect={() => onOpenChange(false)}
        />
      </SheetContent>
    </Sheet>
  );
}
